import tinycolor from 'tinycolor2';

const LibraryFactoryInstance = require('../../factory/library').instance;
const LibraryFactory = require('../../factory/library').factory;
const LibraryPopulator = require('../../factory/library/library-populator');
const UserInfoFactory = require('../../factory/user-info');
const paletteTemplate = require('./palette-selector.hbs');
const tableBorderTemplate = require('./table-border-target.hbs');
const addMoreButtonTemplate = require('./add-more-button.hbs');
const advice = require('../../utils/advice');
const ElementColorRestrictions = require('./element-color-restrictions');

const colorsPerLine = 6;

const ColorSelector = function () {
  this.$addColorButton = null;
  this.selectedColor = null;
  this.$wrapper = null;
  this.callback = null;
  this.libraryColors = null;
  this.libraryPalettes = [];
  this.$elem = null;
};

ColorSelector.prototype.isLibraryShown = false;
ColorSelector.prototype = new ColorSelector();
ColorSelector.prototype.constructor = ColorSelector;

ColorSelector.prototype.initialize = function (hasLibrary, $elem, buttonType) {
  this.hasLibrary = hasLibrary;
  this.editor = INKIVE.Tool.getInstance().editor;
  this.$elem = $elem;
  this.restrictedColors = [];
  this.buttonType = buttonType;

  _.bindAll(this,
    'favColors',
    'renderColors',
    'setColor',
    'addColor',
    'getColorHtml',
    'addColorPicker',
    'addColorToList',
    'addColorToLibrary',
    'selectPaletteEvent'
  );

  return this.hasLibrary ? this._loadColors() : Promise.resolve();
};

ColorSelector.prototype._loadColors = async function () {
  const promises = [];

  this.libraryInstance = INKIVE.Helpers.isDesygnerProject()
    ? LibraryFactoryInstance
    : new LibraryFactory('member');

  promises.push(this._loadMembershipPalettes());
  INKIVE.Helpers.isDesygnerProject() || promises.push(this._loadCompanyPalettes());

  await Promise.all(promises);
  const colors = await this.libraryInstance.get('colors');

  let colorRestrictionsType = this.buttonType;
  if (['cell_highlight', 'cell_foreground'].includes(this.buttonType)) {
    colorRestrictionsType = 'text';
  } else if (['cell_lines', 'cell_background', 'cell_color', 'cell_border'].includes(this.buttonType)) {
    colorRestrictionsType = 'table';
  }

  if (['fill', 'stroke'].includes(this.buttonType)) {
    colorRestrictionsType = '';
  }

  const elemColorRestrictions = new ElementColorRestrictions(
    this.$elem.attr('id'), this.libraryPalettes, colors, colorRestrictionsType
  );

  this.restrictedColors = elemColorRestrictions.getRestrictedColors();
  this.hasLibrary = !this.restrictedColors.length;
};

ColorSelector.prototype._loadMembershipPalettes = function () {
  return this._loadPalettes(this.libraryInstance, 0, false, true);
};

ColorSelector.prototype._loadCompanyPalettes = async function () {
  const userInfo = await UserInfoFactory.getMembership();

  return this._loadPalettes(LibraryFactoryInstance, 1, true, userInfo.permissions.assets_manage);
};

ColorSelector.prototype._loadPalettes = async function (libraryInstance, defaultPaletteId, isCompany, canAdd) {
  const [palettes, colors] = await Promise.all([
    libraryInstance.get('palettes'),
    libraryInstance.get('colors')
  ]);

  for (const palette of palettes) {
    palette.canAdd = canAdd;
    palette.isCompany = isCompany;
  }

  const name = isCompany ? INKIVE.TranslateHelper.translate('tool.general.company') + ' - ' : '';

  this.libraryPalettes.push({
    id: defaultPaletteId,
    isCompany,
    canAdd,
    data: {
      colors: colors.filter(c => !c.data.palette),
      name: name + INKIVE.TranslateHelper.translate('tool.library.default_palette')
    }
  }, ...palettes);
};

ColorSelector.prototype.favColors = function ($elem, callback) {
  this.callback = callback;
  $elem.css({
    'overflow-y': 'initial',
    'overflow-x': 'initial'
  });

  this.$wrapper = $elem.find('.colorLineWrapper');
  this.renderColors();

  if (!this.restrictedColors.length) {
    this.addColorPicker();

    $('#jpickerWrapper' + this.$wrapper.parents('a').attr('type')).on('color-picker-loaded', _.bind(function (event) {
      this.selectedColor = this.$wrapper.parents('a').find('[name=hex]').val();
    }, this));

    this.toggleLibraryColors(true);
  }
};

ColorSelector.prototype.renderColors = function () {
  this.$wrapper.empty();

  if (this.buttonType === 'cell_lines') {
    const $container = this.$wrapper;
    $container.append(tableBorderTemplate({}));
    $container.append('<div class="horizontal-separator"></div>');

    $container.find('.table-cell-border-color-target select').on('change', this.selectTargetBorderEvent);
  }

  if (this.hasLibrary) {
    this.$wrapper.append('<div class="ui-dropdown-content-title color-title library-title">' +
      INKIVE.TranslateHelper.translate('popupmenu.dropdown_title.library_colors') +
      '<i class="title-arrow desygner-icon-arrow-down"></i>' +
      '</div>');

    const $libraryColorContainer = $('<div class="library-colors-container"></div>');
    this.$wrapper.append($libraryColorContainer);

    const palettes = this.libraryPalettes
      .map(p => ({
        key: p.id,
        tag: p.data.name
      }));

    $libraryColorContainer.append(paletteTemplate(palettes));

    const $paletteSelect = $libraryColorContainer.find('.palette-container > select');
    $paletteSelect.val(this.libraryInstance.popupMenuColorPalette);
    $paletteSelect.on('change', this.selectPaletteEvent);

    const $libraryColors = $('<div/>');
    $libraryColorContainer.append($libraryColors);

    this.selectPaletteEvent();

    $libraryColorContainer.before('<div class="library-loader"/>');

    this.$wrapper.append('<div class="horizontal-separator"></div>');
  }

  let editorUsedColors = this.editor.getEditorUsedColors();

  this.$wrapper.append('<div class="ui-dropdown-content-title color-title template-title">' +
    INKIVE.TranslateHelper.translate('popupmenu.dropdown_title.template_colors') +
    (this.hasLibrary ? '<i class="title-arrow desygner-icon-arrow-up"></i>' : '') +
    '</div>');

  const $templateColorContainer = $('<div class="template-colors-container"></div>');
  const $templateColors = $('<div/>');
  const $recentColors = $('<div/>');

  $templateColorContainer.append($templateColors);
  $templateColorContainer.append($recentColors);
  this.$wrapper.append($templateColorContainer);

  editorUsedColors = _.last(editorUsedColors, colorsPerLine);
  _.each(editorUsedColors, _.bind(function (c) {
    this.addColor(c, 'editor', $templateColors);
  }, this));

  this.recentColors = _.difference(this.editor.getFavColors(), editorUsedColors);
  this.recentColors = _.last(this.recentColors, 5);
  _.each(this.recentColors, _.bind(function (c) {
    this.addColor(c, 'recent', $recentColors);
  }, this));

  if (this.restrictedColors.length) {
    _.each(this.restrictedColors, _.bind(function (c) {
      this.addColor(c, 'restricted', $templateColors);
    }, this));
  } else {
    const $addMoreButton = $('<div class="palette-item addmore-recent-color addmore" data-attr="fill fill-opacity" ' +
      'data-title="' + INKIVE.TranslateHelper.translate('popupmenu.color_picker.add_more_recent') +
      '"><i class="desygner-icon-plus"></i></div>');
    $recentColors.append($addMoreButton);
    $addMoreButton.off('click').on('click', this.addColorToList);
  }

  this.hasLibrary && this.$wrapper.find('.color-title').off('click').on('click', _.bind(function () {
    this.toggleLibraryColors();
    return false;
  }, this));

  INKIVE.Helpers.initTooltips(this.$wrapper, { placement: 'bottom' });
};

ColorSelector.prototype.addColorToList = function (applyColor, isLibrary) {
  if (!this.selectedColor) {
    return false;
  }

  isLibrary || this.editor.setFavColor(this.selectedColor);

  const colorButtonType = this.$wrapper.parents('a').attr('type');
  const selectedElements = INKIVE.Tool.getInstance().editor.canvas.getSelectedElems();
  const selectedIds = selectedElements.map(elem => elem.id);

  INKIVE.EditorFacade.editor.clearSelection();
  INKIVE.EditorFacade.select(selectedIds);

  this.$elem.is('.subelement') && INKIVE.Menus.elementPopupMenu.render(this.$elem);

  const $colorMapLink = $('[data-action="colormap-circle"][type=' + colorButtonType + ']');
  $colorMapLink.find('input[type=hidden]').val(this.selectedColor);

  _.defer(() => {
    $colorMapLink.trigger('click', { keepHidden: applyColor });
    applyColor === true && $colorMapLink.find('.palette-item[data-color=' + this.selectedColor + ']')
      .trigger('click', { keepHidden: applyColor });
  });
};

ColorSelector.prototype.addColorToLibrary = function () {
  if (!this.selectedColor) {
    return false;
  }

  const $libraryColorsContainer = this.$wrapper.find('.library-colors-container');

  if ($libraryColorsContainer.is(':visible')) {
    $libraryColorsContainer.css('opacity', 0);
    INKIVE.Modal.loader(this.$wrapper.find('.library-loader'));
  } else {
    INKIVE.Modal.togglePageLoader(true);
  }

  const item = {
    color_code: this.selectedColor,
    is_dark: tinycolor(this.selectedColor).isDark()
  };
  const palette = _.findWhere(this.libraryPalettes, {
    id: parseInt(this.libraryInstance.popupMenuColorPalette)
  });

  if (palette.id) {
    item.palette = palette.data.id;
  }

  const library = palette.id === 1 ? LibraryFactoryInstance : this.libraryInstance;

  LibraryPopulator.ensureUniquenes('color', item)
    .then(LibraryPopulator.wrappResource, _.bind(onColourError, this, 'add-to-library-duplicity'))
    .then(_.bind(function (item) {
      return library.post('colors', item).then(
        _.bind(onColourAdded, this), _.bind(onColourError, this)
      );
    }, this))
    .always(_.partial(INKIVE.Modal.togglePageLoader, false));

  return false;

  function onColourAdded (data) {
    data && palette.data.colors.push(data.attributes);
    this.addColorToList(true, true);
    advice.show('add-to-library-success');
  }

  function onColourError (message) {
    INKIVE.Modal.loaderClose(this.$wrapper.find('.library-loader'));
    $libraryColorsContainer.css('opacity', 1);
    LibraryPopulator.onError(message);
  }
};

ColorSelector.prototype.setColor = function (color) {
  this.selectedColor = _.isString(color) ? color.toUpperCase() : null;
  return color;
};

ColorSelector.prototype.addColor = function (color, type, $container, smartAsset) {
  if (this.restrictedColors.length && type !== 'restricted') {
    return;
  }

  const item = this.getColorHtml(color, type, smartAsset);
  $container.append(item);

  $(item).off('click').on('click', () => {
    let where = null;

    if (this.buttonType === 'cell_lines') {
      const borderTargetValue = $('.table-cell-border-color-target select').val();
      where = INKIVE.Table.convertBorderNameToFlag(borderTargetValue) || 'lrtb';
    }

    this.callback(item, { where });
  });
};

ColorSelector.prototype.getColorHtml = function (color, type, smartAsset) {
  const tooltip = INKIVE.TranslateHelper.translate(`popupmenu.color_picker.thumb.${type}`, { color });
  const backgroundAttribute = color.toUpperCase() === 'NONE' ? '' : 'style="background: ' + color + ';"';
  const smartAssetAttribute = smartAsset ? `data-smart-asset="${smartAsset}"` : '';

  return $(`
    <div class="palette-item"
      ${smartAssetAttribute}
      data-attr="fill fill-opacity"
      data-color-type="${type}"
      data-color="${color}"
    >
      <span ${backgroundAttribute}
        data-popover="tooltip"
        title="${tooltip}"
        data-original-title="${tooltip}"
        data-title="${tooltip}"
      ></span>
    </div>
  `);
};

ColorSelector.prototype.addColorPicker = function () {
  const $container = this.$wrapper.parent();
  $container.append('<div class="horizontal-separator"></div>');

  $container.append('<div class="ui-dropdown-content-title color-title">' +
    INKIVE.TranslateHelper.translate('popupmenu.dropdown_title.choose_color') +
    '</div>');

  $container.append('<div class="colorpicker-container"/>');

  $container.find('.colorpicker-container').colorpicker({
    isInline: true,
    selectedElement: this.$elem,
    defaultColor: $container.parents('a').find('[name=hex]').val(),
    onSubmit: _.bind(function (color, context, isLibrary) {
      let hex = this.$wrapper.siblings('.colorpicker-container').find('.color-selector-input').val() ? color : '';
      if (!hex || color.toUpperCase() === 'NONE') {
        hex = color;
      }

      this.setColor(hex);
      isLibrary ? this.addColorToLibrary() : this.addColorToList(true);

      if (!hex) return false;
    }, this),
    onChange: this.setColor,
    onCancel: _.bind(function () {
      this.$addColorButton.remove();
    }, this)
  });

  $container.append('<div class="horizontal-separator color-separator"/>');
};

ColorSelector.prototype.toggleLibraryColors = function (keepLibraryValue) {
  if (!keepLibraryValue) {
    ColorSelector.prototype.isLibraryShown = this.isLibraryShown = !this.isLibraryShown;
  }

  const shownSelectorName = this.isLibraryShown ? 'library' : 'template';
  const hiddenSelectorName = this.isLibraryShown ? 'template' : 'library';

  this.$wrapper.find('.title-arrow').removeClass('desygner-icon-arrow-down desygner-icon-arrow-up');

  this.$wrapper.find('.' + hiddenSelectorName + '-colors-container').slideUp();
  this.$wrapper.find('.' + shownSelectorName + '-colors-container').slideDown();
  this.$wrapper.find('.' + shownSelectorName + '-title .title-arrow').addClass('desygner-icon-arrow-up');
  this.$wrapper.find('.' + hiddenSelectorName + '-title .title-arrow').addClass('desygner-icon-arrow-down');

  this.$wrapper.find('.palettes').val(this.libraryInstance.popupMenuColorPalette).trigger('change');
};

ColorSelector.prototype.selectPaletteEvent = function (event) {
  if (event) this.libraryInstance.popupMenuColorPalette = $(event.target).val();

  const $libraryColorContainer = this.$wrapper.find('.library-colors-container > div:not(.palette-container)');
  const selectedPaletteId = parseInt(this.libraryInstance.popupMenuColorPalette);
  const selectedPalette = this.libraryPalettes.find(({ id }) => id === selectedPaletteId);

  $libraryColorContainer.find('.palette-item').remove();

  const displayColors = selectedPalette.data.colors
    .slice(0, colorsPerLine)
    .reverse();

  for (const color of displayColors) {
    this.addColor(
      color.data.color_code, 'library', $libraryColorContainer,
      selectedPalette.isCompany ? color.id : null
    );
  }

  this.libraryColors = displayColors.map(({ data }) => data.color_code.toUpperCase());

  if (this.libraryColors.length < colorsPerLine && selectedPalette.canAdd) {
    const $addMoreLibraryButton = $(addMoreButtonTemplate());
    $libraryColorContainer.append($addMoreLibraryButton);
    $addMoreLibraryButton.off('click').on('click', this.addColorToLibrary);
  }
};

module.exports = ColorSelector;
